import { hasAccess } from '@/lib/utils';
import apis from '@/services/api-services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';

export const getSubscriptionPlans = ({ agent }: { agent: string }) => ({
  queryKey: ['get-subscription-plans', agent],
  queryFn: async () => {
    const response = await apis.subscriptions.getSubscriptionPlans(agent);
    return response.data;
  },
});

export const getActiveUserSubscription = () => ({
  queryKey: ['get-active-user-subscription'],
  queryFn: async () => {
    const response = await apis.subscriptions.getActiveUserSubscription();
    return response.data;
  },
});

export const getActiveUserStats = () => ({
  queryKey: ['get-active-user-stats'],
  queryFn: async () => {
    const response = await apis.diana.cre.getUserStats();
    return response.data;
  },
});

export const useGetUserStats = () => {
  const { data: session } = useSession();
  const hasDianaAccess = hasAccess('diana', session);
  const hasCreAccess = hasAccess('cre', session);

  const {
    data,
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    status,
    isFetching,
  } = useQuery({
    ...getActiveUserStats(),
    enabled: hasDianaAccess || hasCreAccess,
  });

  return {
    data,
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    status,
    isFetching,
  };
};

export const useGetSubscriptionPlans = ({ agent }: { agent: string }) => {
  const {
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    data,
    status,
    isFetching,
  } = useQuery(getSubscriptionPlans({ agent }));
  return {
    data,
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    status,
    isFetching,
  };
};

export const useGetActiveUserSubscription = () => {
  const {
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    data,
    status,
    isFetching,
  } = useQuery(getActiveUserSubscription());
  return {
    data,
    isPending,
    refetch,
    isLoading,
    error,
    isSuccess,
    isFetched,
    isRefetching,
    isError,
    status,
    isFetching,
  };
};

interface InitializePaymentData {
  plan_id: string;
}

export const useInitializePayment = () => {
  const {
    mutate,
    mutateAsync,
    isPending,
    isError,
    isSuccess,
    error,
    data,
    status,
  } = useMutation({
    // mutationFn is the function that will be called when the mutation is triggered.
    mutationFn: (paymentData: InitializePaymentData) =>
      apis.subscriptions.initialisePayment(paymentData),

    // You can add onSuccess, onError, onSettled callbacks here
    // for side-effects after the mutation completes.
    onSuccess: (responseData) => {
      console.log('Payment initialized successfully:', responseData.data);
      // For example, you could redirect the user to the payment gateway here.
      // window.location.href = responseData.data.authorization_url;
    },
    onError: (err) => {
      console.error('Error initializing payment:', err);
    },
  });

  return {
    initializePayment: mutate, // The function to call to trigger the mutation
    initializePaymentAsync: mutateAsync, // An async version of the mutation function
    isPending, // True if the mutation is currently running
    isError, // True if the mutation failed
    isSuccess, // True if the mutation was successful
    error, // The error object if the mutation failed
    data: data?.data, // The data returned from the successful mutation
    status, // The status of the mutation ('idle', 'pending', 'success', 'error')
  };
};
